// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Faithful port of the approved mockup's client-side logic
// (docs/design/report-builder-mockup.html): the Impact meter, the
// describe-it/AI proposer + heavy-query pushback, the field-class model, the
// preview-as roles, the computed-column presets, and the starter templates.

// Baseline per-table row estimates that drive the Impact heuristic. The real
// truthful estimate is SHOWPLAN against the replica (phase 4); this matches the
// mockup's signature behaviour until then.
export const TABLE_ROWS: Record<string, number> = {
  rx_items: 642, rx_claims: 2227, rx_pricing: 624, patients: 629,
}
export const tableRows = (t: string) => TABLE_ROWS[t] ?? 600

// Field sensitivity class. Mirrors migration 144's pattern: PHI from catalog
// sensitivity; financial inferred from the column name; else operational.
const FINANCIAL = /cost|paid|price|amount|reimburs|dispensing_fee|margin/i
export function classOf(column: string, sensitivity?: string): string {
  if (sensitivity === 'phi' || sensitivity === 'phi_high') return 'phi'
  if (FINANCIAL.test(column)) return 'financial'
  return 'operational'
}
export const CLASS_LABEL: Record<string, string> = {
  financial: 'Financial', phi: 'PHI / identity', clinical: 'Clinical', operational: 'Operational',
}

// Preview-as roles. Class not listed => masked for that role.
export const ROLE_CLASSES: Record<string, string[]> = {
  'Super-admin (you)': ['operational', 'clinical', 'phi', 'financial'],
  'Pharmacy manager': ['operational', 'clinical', 'phi'],
  'Pharmacy technician': ['operational', 'clinical'],
  'Billing clerk': ['operational', 'financial'],
}
export const PREVIEW_ROLES = Object.keys(ROLE_CLASSES)
export const roleCanSee = (role: string, cls: string) => (ROLE_CLASSES[role] ?? []).includes(cls)

// Computed-column presets (backend resolves the SQL; these drive the buttons + class).
export const COMPUTED: Record<string, { key: string; label: string; cls: string; needs: string[] }> = {
  margin_dollar: { key: 'margin_dollar', label: 'Margin $', cls: 'financial', needs: ['rx_claims'] },
  margin_pct: { key: 'margin_pct', label: 'Margin %', cls: 'financial', needs: ['rx_claims'] },
  pa_rate: { key: 'pa_rate', label: 'PA rate %', cls: 'operational', needs: ['rx_claims'] },
}

// ---- Impact meter (the signature) ----
export interface ImpactInput { tables: string[]; hasAgg: boolean; anyPhi: boolean; filterCount: number; columnCount: number }
export interface Impact {
  cost: number; ms: number; rowsScanned: number; nTables: number
  level: 'green' | 'amber' | 'red'; pct: number; drivers: string[]; baseRows: number
}
export function computeImpact(i: ImpactInput): Impact {
  const tables = i.tables
  const nTables = Math.max(1, tables.length)
  const baseRows = tables.length ? Math.max(...tables.map(tableRows)) : 0
  let cost = baseRows * Math.pow(1.7, nTables - 1)
  if (i.hasAgg) cost *= 1.5
  if (i.anyPhi) cost *= 1.05
  const fan = Math.pow(1.7, nTables - 1)
  const filterFactor = Math.pow(0.55, i.filterCount)
  cost *= filterFactor
  const rowsScanned = Math.round(baseRows * fan * filterFactor)
  cost = Math.round(cost)
  const ms = Math.max(60, Math.round(cost * 0.9))
  let level: Impact['level'] = 'green'
  if (cost > 2600) level = 'amber'
  if (cost > 9000) level = 'red'
  let pct = Math.min(96, Math.round(cost / 130))
  pct = level === 'green' ? Math.min(34, pct)
      : level === 'amber' ? Math.min(72, Math.max(38, pct))
      : Math.min(96, Math.max(74, pct))
  const drivers: string[] = [`${nTables} table${nTables > 1 ? 's' : ''} joined`]
  if (i.hasAgg) drivers.push(`aggregating ~${baseRows.toLocaleString()} rows`)
  if (!i.filterCount && i.columnCount) drivers.push('no filter applied')
  else if (i.filterCount) drivers.push(`${i.filterCount} filter${i.filterCount > 1 ? 's' : ''} narrowing it`)
  return { cost, ms, rowsScanned, nTables, level, pct, drivers, baseRows }
}

// ---- describe-it / AI proposer + heavy-query pushback ----
export type TemplateKey = 'costxins' | 'pa' | 'margin' | 'refills' | 'payers'
export interface Proposal { key: TemplateKey; heavy: boolean }
export function aiPropose(query: string): Proposal {
  const s = query.toLowerCase()
  const key: TemplateKey =
    (/cost|price/.test(s) && /insur|payer|payor/.test(s)) ? 'costxins'
    : /prior|authoriz|\bpa\b/.test(s) ? 'pa'
    : /margin|profit/.test(s) ? 'margin'
    : /refill/.test(s) ? 'refills'
    : 'payers'
  const heavy =
    (/full history|all[- ]?time|every patient|all patients|each patient|entire history|whole database/.test(s)
      || (/all|every|entire|whole/.test(s) && /patient|history|record|time/.test(s)))
    && !/last|past|recent|day|week|month|quarter|year|90|30|today/.test(s)
  return { key, heavy }
}

// ---- starter templates (resolved to real field ids at apply-time by column name) ----
export interface TemplateDef {
  name: string; desc: string
  cols: [string, string][]            // [source_table, column_name]
  aggs: Record<string, string>        // column_name -> agg
  group: string[]                     // column_names
  computed: string[]
  filters: [string, string, string][] // [column_name, op, value]
}
export const TEMPLATES: Record<TemplateKey, TemplateDef> = {
  costxins: {
    name: 'Drug cost by insurance', desc: 'Cost, volume, last fill & PA per drug × payer',
    cols: [['rx_items', 'drug_name'], ['rx_claims', 'payor_name'], ['rx_items', 'rx_number'], ['rx_items', 'date_written'], ['rx_claims', 'aquisition_cost']],
    aggs: { rx_number: 'count', date_written: 'max', aquisition_cost: 'avg' }, group: ['drug_name', 'payor_name'], computed: ['pa_rate'], filters: [],
  },
  pa: {
    name: 'PA by Payer & Drug', desc: 'Which plans require PA for which drugs',
    cols: [['rx_claims', 'payor_name'], ['rx_claims', 'bin_number'], ['rx_items', 'drug_name']],
    aggs: {}, group: ['payor_name', 'bin_number', 'drug_name'], computed: ['pa_rate'], filters: [],
  },
  margin: {
    name: 'Drug Margin Analysis', desc: 'Profit by drug and dosage form',
    cols: [['rx_items', 'drug_name'], ['rx_items', 'drug_form'], ['rx_claims', 'gross_amount_paid'], ['rx_claims', 'aquisition_cost']],
    aggs: { gross_amount_paid: 'sum', aquisition_cost: 'sum' }, group: ['drug_name', 'drug_form'], computed: ['margin_dollar', 'margin_pct'], filters: [],
  },
  refills: {
    name: 'Refills Due', desc: 'Active therapies and days supply',
    cols: [['rx_items', 'rx_number'], ['rx_items', 'drug_name'], ['rx_items', 'days_supply'], ['rx_items', 'payer_state']],
    aggs: {}, group: [], computed: [], filters: [],
  },
  payers: {
    name: 'Top Payers by Volume', desc: 'Claim counts by payer',
    cols: [['rx_claims', 'payor_name'], ['rx_claims', 'bin_number']],
    aggs: {}, group: ['payor_name', 'bin_number'], computed: [], filters: [],
  },
}
